/**
 * LINE Group Chat API Client (Phase 3)
 * Manages group-to-business token linking
 */

import axios, { getBusinessId } from '@/lib/api/axios-instance';

const BASE_URL = '/api/v2/business/line-groups';

// ============================================================================
// Types
// ============================================================================

export interface LineGroupMapping {
  id: string;
  group_id: string;
  group_type: 'group' | 'room';
  business_id: string;
  business_name?: string;
  branch_id: string | null;
  branch_name?: string | null;
  linked_by_line_user_id?: string;
  is_active: boolean;
  created_at: string;
}

export interface CreateLinkTokenResponse {
  token: string;
  branch_id: string | null;
  expires_at: string;
  instructions: string;
}

// ============================================================================
// API Functions
// ============================================================================

export const lineGroupsAPI = {
  /**
   * Create a new link token (GLK_xxx) for binding a LINE group to a business.
   * Optionally scoped to a specific branch.
   */
  async createToken(businessId?: string, branchId?: string | null): Promise<CreateLinkTokenResponse> {
    const bid = businessId || getBusinessId();
    if (!bid) throw new Error('Business ID not found. Please login again.');
    const response = await axios.post<{ success: boolean; data: CreateLinkTokenResponse }>(
      `${BASE_URL}/token`,
      { business_id: bid, ...(branchId ? { branch_id: branchId } : {}) }
    );
    return response.data.data;
  },

  /**
   * List all linked groups for the current business
   */
  async getAll(businessId?: string): Promise<LineGroupMapping[]> {
    const bid = businessId || getBusinessId();
    const params = bid ? { business_id: bid } : {};
    const response = await axios.get<{ success: boolean; data: LineGroupMapping[] }>(
      BASE_URL,
      { params }
    );
    return response.data.data ?? [];
  },

  /**
   * Unlink (soft-delete) a group mapping by ID
   */
  async delete(id: string): Promise<void> {
    await axios.delete(`${BASE_URL}/${id}`);
  },

  /**
   * Update the branch assignment for an existing group mapping.
   * Pass null to clear the branch override (reverts to auto-resolve).
   */
  async updateBranch(id: string, branchId: string | null): Promise<{ id: string; branch_id: string | null }> {
    const response = await axios.patch<{ success: boolean; data: { id: string; branch_id: string | null } }>(
      `${BASE_URL}/${id}/branch`,
      { branch_id: branchId }
    );
    return response.data.data;
  },
};
